'use client';

import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/buttons/button";
import { MainGreenButton } from '@/components/buttons/main-green-button';
import { LoadingSpinner } from '@/components/ui/spinner';
import { PendingInvitation } from '../types';
import { acceptTeamInvitation, declineTeamInvitation } from '@/services/teams-client.service';
import { useSession } from '@descope/nextjs-sdk/client';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Users } from 'lucide-react';

interface TeamInvitationPopupProps {
  invitation: PendingInvitation;
  onResolved: () => void;
}

export const TeamInvitationPopup: React.FC<TeamInvitationPopupProps> = ({
  invitation,
  onResolved,
}) => {
  const { sessionToken } = useSession();
  const queryClient = useQueryClient();
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const isLoading = isAccepting || isDeclining;

  const inviterName = invitation.invitedBy
    ? `${invitation.invitedBy.firstName || ''} ${invitation.invitedBy.lastName || ''}`.trim()
    : null;

  const handleAccept = async () => {
    if (!sessionToken) return;
    setIsAccepting(true);
    try {
      await acceptTeamInvitation(invitation.team.id, sessionToken);
      queryClient.invalidateQueries({ queryKey: ['userTeams'] });
      queryClient.invalidateQueries({ queryKey: ['userDetails'] });
      queryClient.invalidateQueries({ queryKey: ['pendingInvitations'] });
      queryClient.invalidateQueries({ queryKey: ['teamPreferences'] });
      queryClient.invalidateQueries({ queryKey: ['totalBalance'] });
      queryClient.invalidateQueries({ queryKey: ['personalBalance'] });
      toast.success(`You have joined ${invitation.team.name}`);
      onResolved();
    } catch (error: any) {
      toast.error(error.message || 'Failed to accept invitation');
    } finally {
      setIsAccepting(false);
    }
  };

  const handleDecline = async () => {
    if (!sessionToken) return;
    setIsDeclining(true);
    try {
      await declineTeamInvitation(invitation.team.id, sessionToken);
      queryClient.invalidateQueries({ queryKey: ['pendingInvitations'] });
      toast.success('Invitation declined');
      onResolved();
    } catch (error: any) {
      toast.error(error.message || 'Failed to decline invitation');
    } finally {
      setIsDeclining(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={() => {}}>
      <DialogContent className="z-modal" onPointerDownOutside={(e) => e.preventDefault()}>
        <DialogHeader>
          <DialogTitle>Team Invitation</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg">
            <div className="flex items-center justify-center w-10 h-10 bg-[#E8E7FD] rounded-full">
              <Users className="w-5 h-5 text-[#5049E5]" />
            </div>
            <div>
              <p className="font-medium text-sm">{invitation.team.name}</p>
              <p className="text-xs text-gray-500">
                Role: {invitation.role.charAt(0).toUpperCase() + invitation.role.slice(1)}
              </p>
            </div>
          </div>
          <p className="text-sm text-gray-700">
            {inviterName ? (
              <><span className="font-medium">{inviterName}</span> invited you to join <span className="font-medium">{invitation.team.name}</span>.</>
            ) : (
              <>You have been invited to join <span className="font-medium">{invitation.team.name}</span>.</>
            )}
          </p>
          <p className="text-xs text-gray-500">
            By accepting, you will become a {invitation.role} of this team and gain access to shared team resources.
          </p>
          <div className="flex justify-end gap-2 pt-2">
            <Button
              variant="outline"
              onClick={handleDecline}
              disabled={isLoading}
            >
              {isDeclining ? <LoadingSpinner /> : 'Decline'}
            </Button>
            <MainGreenButton onClick={handleAccept} disabled={isLoading}>
              {isAccepting ? <LoadingSpinner /> : 'Accept Invitation'}
            </MainGreenButton>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
